import { useCallback, useEffect, useState } from 'react';
import { api, describeError } from '../api';
import { Message, Pill, Section, fmtBytes, fmtTime } from '../components/ui';
import { useSession } from '../session';

interface VaultInfo { size_bytes: number; quota_bytes: number; schema_version: string; seeded_at: string | null; oplog_seq: number; oplog_min_seq: number }
interface Op { seq: number; entity: string; entity_id: string; op: string; ts: string }
interface SyncRes { ops: Op[]; next_since: number; has_more: boolean; latest_seq: number }

export function SyncPage() {
  const { env, me } = useSession();
  const [info, setInfo] = useState<VaultInfo | null>(null);
  const [since, setSince] = useState('0');
  const [limit, setLimit] = useState('50');
  const [res, setRes] = useState<SyncRes | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;
    try { setInfo((await api<VaultInfo>('/api/me/vault')).data); } catch (e) { setErr(describeError(e)); }
  }, [me]);
  useEffect(() => { void load(); }, [load, env]);

  const pull = async (s = since) => {
    setErr(null);
    try {
      const { data } = await api<SyncRes>(`/api/sync?since=${encodeURIComponent(s)}&limit=${encodeURIComponent(limit)}`);
      setRes(data);
      await load();
    } catch (e) { setErr(describeError(e)); }
  };

  const pct = info ? Math.min(100, Math.round((info.size_bytes / Math.max(1, info.quota_bytes)) * 1000) / 10) : 0;
  return (
    <div className="space-y-4">
      <Section title="Vault" actions={<button className="btn-ghost" disabled={!me} onClick={() => void load()}>刷新</button>}>
        {!me && <Message text="请先登录" />}
        {info && (
          <div className="grid gap-3 text-xs text-zinc-300 sm:grid-cols-3">
            <div><div className="label">存储</div>{fmtBytes(info.size_bytes)} / {fmtBytes(info.quota_bytes)}<div className="mt-1 h-1.5 rounded bg-zinc-800"><div className="h-1.5 rounded bg-indigo-500" style={{ width: `${pct}%` }} /></div></div>
            <div><div className="label">schema / seeded</div>{info.schema_version} · {fmtTime(info.seeded_at)}</div>
            <div><div className="label">oplog</div>latest <span className="font-mono">{info.oplog_seq}</span> · min retained <span className="font-mono">{info.oplog_min_seq}</span></div>
          </div>
        )}
        <Message text={err} kind="error" />
      </Section>
      <Section
        title="增量同步 GET /api/sync"
        actions={
          <>
            <input className="input w-28" value={since} onChange={(e) => setSince(e.target.value)} placeholder="since" />
            <input className="input w-20" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="limit" />
            <button className="btn" disabled={!me} onClick={() => void pull()}>拉取</button>
            {res && <button className="btn-ghost" disabled={!res.has_more && res.next_since === Number(since)} onClick={() => { setSince(String(res.next_since)); void pull(String(res.next_since)); }}>继续 since={res.next_since}</button>}
            {info && <button className="btn-ghost" onClick={() => setSince(String(info.oplog_seq))}>since = latest</button>}
          </>
        }
      >
        <div className="text-xs text-zinc-500">同一实体多次变更合并为最后一次；since 早于 min retained → 409 LIBRARY_SYNC_RESET（全量重拉）。首次接入：先读 /api/me/vault 的 oplog_seq 作为 since，再拉列表。</div>
        {res && (
          <>
            <div className="flex flex-wrap gap-2 text-xs text-zinc-300"><Pill>ops {res.ops.length}</Pill><Pill>next_since {res.next_since}</Pill><Pill tone={res.has_more ? 'amber' : 'emerald'}>has_more {String(res.has_more)}</Pill><Pill>latest_seq {res.latest_seq}</Pill></div>
            <div className="max-h-96 overflow-auto rounded-lg border border-zinc-800">
              <table className="w-full text-left text-xs">
                <thead className="bg-zinc-900 text-zinc-400"><tr><th className="px-2 py-1">seq</th><th className="px-2 py-1">entity</th><th className="px-2 py-1">entity_id</th><th className="px-2 py-1">op</th><th className="px-2 py-1">ts</th></tr></thead>
                <tbody>{res.ops.map((o) => <tr key={o.seq} className="border-t border-zinc-800"><td className="px-2 py-1 font-mono">{o.seq}</td><td className="px-2 py-1">{o.entity}</td><td className="px-2 py-1 font-mono text-zinc-400">{o.entity_id}</td><td className="px-2 py-1">{o.op === 'delete' ? <Pill tone="rose">delete</Pill> : <Pill tone="emerald">upsert</Pill>}</td><td className="px-2 py-1 text-zinc-500">{fmtTime(o.ts)}</td></tr>)}</tbody>
              </table>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
